<template>
  <div v-if="planSale">
    <ShowPageHeader :backLabel="t('back')" backRoute="/plan-sales" :title="planSale.friendlyId">
      <template #badges>
        <FieldSpan :value="planSale.category" style-to-add="bg-blue-100 text-blue-800" />
        <FieldSpan
          :value="planSale.status"
          :style-to-add="statusBadgeClass(planSale)"
          translation-prefix="planSale.status"
        />
        <FieldSpan
          v-if="planSale.compliant === true"
          :value="t('compliant')"
          style-to-add="text-green-500 bg-green-100 border border-green-300"
        />
        <FieldSpan
          v-if="planSale.compliant === false"
          :value="t('non_compliant')"
          style-to-add="text-red-500 bg-red-100 border border-red-300"
        />
      </template>
      <template #actions>
        <div v-if="isUpdatable(planSale)">
          <SecondaryButton @click="openEditModal = true">✏️ {{ t("edit") }}</SecondaryButton>
          <UpdateModal
            :open="openEditModal"
            :planSale="planSale"
            @close="openEditModal = false"
            @update-plan-sale="updatePlanSale"
          />
        </div>
        <div v-if="!isCancelled(planSale)">
          <SecondaryButton @click="openCancellationModal = true">{{ t("cancel") }}</SecondaryButton>
          <CancellationModal
            :open="openCancellationModal"
            :planSale="planSale"
            @close="openCancellationModal = false"
            @cancel-plan-sale="cancelPlanSale"
          />
        </div>
        <SecondaryButton v-if="isActive(planSale)" @click="sendConfirmation">
          ✉️ {{ t("send_confirmation") }}
        </SecondaryButton>
      </template>
    </ShowPageHeader>

    <PlanSaleSummaryCard class="mt-6" :planSale="planSale" />

    <TabsNavigation :tabs="tabs" class="my-6" />

    <InsuredProductsInformations v-if="currentTab === 'products'" class="mt-6" :products="planSale.products" />
    <CustomerInformations v-if="currentTab === 'customer'" class="mt-6" :customer="planSale.customer" />
    <PlanSaleBillingDetails
      v-if="currentTab === 'payments'"
      class="mt-6"
      :planSaleBillings="planSale.planSaleBillings"
      :chargedBy="planSale.chargedBy"
    />
    <RetributionDetails v-if="currentTab === 'retributions'" class="mt-6" :retribution="retribution" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { useI18n } from "vue-i18n";
import { FieldSpan, SecondaryButton, ShowPageHeader, TabsNavigation } from "@estaly/ui";

import CancellationModal from "@/components/plan_sales/CancellationModal.vue";
import CustomerInformations from "@/components/plan_sales/CustomerInformations.vue";
import InsuredProductsInformations from "@/components/plan_sales/InsuredProductsInformations.vue";
import PlanSaleBillingDetails from "@/components/plan_sales/PlanSaleBillingDetails.vue";
import PlanSaleSummaryCard from "@/components/plan_sales/PlanSaleSummaryCard.vue";
import RetributionDetails from "@/components/plan_sales/RetributionDetails.vue";
import UpdateModal from "@/components/plan_sales/UpdateModal.vue";

const SHOW_INCLUDES = [
  "seller",
  "cancellation",
  "plan_sale_billings",
  "store",
  "customer",
  "plan_sale_billings.commissions",
  "plan_sale_billings.retribution",
  "plan_sale_product",
  "membership_agreement",
];

export default {
  name: "PlanSaleDetails",

  components: {
    FieldSpan,
    SecondaryButton,
    ShowPageHeader,
    TabsNavigation,
    CancellationModal,
    CustomerInformations,
    InsuredProductsInformations,
    PlanSaleBillingDetails,
    PlanSaleSummaryCard,
    RetributionDetails,
    UpdateModal,
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  data() {
    return {
      planSale: null,
      planSaleId: this.$route.params.id,
      openEditModal: false,
      openCancellationModal: false,
    };
  },

  computed: {
    ...mapGetters("planSales", ["isActive", "isCancelled", "isUpdatable", "statusBadgeClass"]),

    currentTab() {
      return this.$route.query.tab || "customer";
    },

    tabs() {
      return [
        { name: "customer", displayName: this.t("tab_customer"), query: { tab: "customer" } },
        { name: "products", displayName: this.t("tab_products"), query: { tab: "products" } },
        { name: "retributions", displayName: this.t("tab_retributions"), query: { tab: "retributions" } },
        { name: "payments", displayName: this.t("tab_payments"), query: { tab: "payments" } },
      ];
    },

    retribution() {
      return this.planSale?.planSaleBillings?.[0]?.retribution;
    },
  },

  async mounted() {
    await this.getPlanSale();
  },

  methods: {
    ...mapActions("notifications", ["notify"]),
    ...mapActions("planSales", {
      fetchPlanSale: "fetch",
      updatePlanSaleRequest: "update",
      cancelPlanSaleRequest: "cancel",
      sendConfirmationEmail: "sendConfirmationEmail",
    }),

    async getPlanSale() {
      const planSale = await this.fetchPlanSale({ id: this.planSaleId, includes: SHOW_INCLUDES });
      if (!planSale) return;

      this.planSale = planSale;
    },

    async updatePlanSale(planSaleInfo) {
      const response = await this.updatePlanSaleRequest({ id: this.planSaleId, planSale: planSaleInfo });
      if (!response?.success) return;

      this.openEditModal = false;
      await this.getPlanSale();
      await this.notify({ category: "simple", type: "success", text: this.t("updated") });
    },

    async cancelPlanSale(cancellation) {
      const response = await this.cancelPlanSaleRequest({ id: this.planSale.friendlyId, cancellation });
      if (!response?.success) return;

      this.openCancellationModal = false;
      await this.getPlanSale();
      await this.notify({ category: "simple", type: "success", text: this.t("cancelled") });
    },

    async sendConfirmation() {
      if (!confirm(this.t("send_confirmation_prompt"))) return;

      const response = await this.sendConfirmationEmail({ id: this.planSale.friendlyId });
      if (!response?.success) return;

      await this.notify({ category: "simple", type: "success", title: this.t("email_sent") });
    },
  },
};
</script>

<i18n src="./PlanSaleDetails.i18n.json"></i18n>
